import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { MaskLine } from "@/lib/motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-[72px]">
      <div className="absolute inset-0 gold-spot pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-8 items-center min-h-[calc(100vh-72px)] py-16">
        {/* Left copy */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#0F172A]/60 px-4 py-1.5 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
            <span className="kicker text-[#94A3B8]">Now Boarding · SF Bay & Sacramento</span>
          </motion.div>

          <h1 className="font-head font-black tracking-tighter leading-[0.95] text-[#F8FAFC] text-5xl sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.2}>Safety,</MaskLine>
            <MaskLine delay={0.32} className="text-[#D4AF37]">on demand.</MaskLine>
            <MaskLine delay={0.44} className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mt-3 text-[#CBD5E1]">
              Protection, on your terms.
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="font-body text-[#CBD5E1] text-base sm:text-lg max-w-xl mt-8 leading-relaxed"
          >
            BodyGuard EXP is the marketplace connecting real estate agents with vetted, licensed
            security companies for home showings. Book a guard, track their arrival live, and stay
            protected — all from your phone.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <button
              onClick={() => scrollTo("#waitlist")}
              data-testid="hero-join-btn"
              className="group font-head font-bold text-base bg-[#D4AF37] text-[#050914] px-7 h-[54px] rounded-xl hover:bg-[#E4C569] transition-colors duration-200 flex items-center justify-center gap-2"
            >
              Join the Waitlist
              <ArrowRight className="w-4.5 h-4.5 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button
              onClick={() => scrollTo("#how")}
              data-testid="hero-how-btn"
              className="font-head font-bold text-base bg-transparent text-[#D4AF37] border-[1.5px] border-[#D4AF37] px-7 h-[54px] rounded-xl hover:bg-[#292414] transition-colors duration-200"
            >
              How It Works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="flex items-center gap-2 mt-7 text-[#94A3B8] font-body text-sm"
          >
            <MapPin className="w-4 h-4 text-[#D4AF37]" />
            Launching soon in the SF Bay Area & Sacramento. Be first in line.
          </motion.div>
        </div>

        {/* Right image, clipped + parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[420px] sm:h-[520px] lg:h-[600px] w-full"
        >
          <div className="relative h-full w-full rounded-[20px] overflow-hidden border border-[#1E293B]">
            <motion.img
              src={HERO_IMG}
              alt="Licensed protection officer"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-[#050914]/20 to-transparent" />
            <div className="absolute inset-0 bg-[#050914]/20" />
          </div>

          {/* floating stat chip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="absolute -bottom-5 -left-4 sm:left-6 bg-[#0F172A]/90 backdrop-blur-xl border border-[#1E293B] rounded-2xl px-5 py-4 shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
          >
            <div className="font-head font-black text-2xl text-[#D4AF37]">100%</div>
            <div className="font-body text-xs text-[#94A3B8] mt-0.5">Licensed · Insured · Vetted</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
